import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutGrid, Package, Boxes, Gift, CalendarHeart, Truck, Search, ShieldCheck, Pin, Store, ShoppingCart, Upload, Lightbulb, UserX, LifeBuoy, RotateCcw, BarChart3, PackageSearch, Milk, ClipboardList, X, Clock,
} from 'lucide-react';
import { useOrders } from '../context/OrdersContext';
import { useAuth } from '../context/AuthContext';
import { countPendingPayLaters } from '../api/payLaters';
import bhimaLogo from '../assets/bhima-logo.svg';
import styles from './Sidebar.module.css';

const opsLinks = [
  { to: '/products', label: 'Products', icon: Package },
  { to: '/products/bulk', label: 'Bulk Onboarding', icon: Upload },
  { to: '/suggestions', label: 'Suggestions', icon: Lightbulb },
  { to: '/inventory', label: 'Inventory', icon: Boxes },
  { to: '/subscriptions', label: 'Subs Run Sheet', icon: ClipboardList },
  { to: '/subscriptions/packs', label: 'Subs Packs', icon: Milk },
  { to: '/order-lookup', label: 'Order Lookup', icon: Search },
];

const growthLinks = [
  { to: '/promotions', label: 'Promotions', icon: Gift },
  { to: '/events', label: 'Events', icon: CalendarHeart },
  { to: '/delivery-fees', label: 'Delivery Fees', icon: Truck },
];

export default function Sidebar({ mobileOpen = false, onMobileClose }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [pendingPayLatersCount, setPendingPayLatersCount] = useState(0);
  const { queueBadge, openTicketsCount, inProgressTicketsCount, pendingReturnsCount } = useOrders();
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.groups?.includes('Admin');
  const canSeeProductInsight = isAdmin || user?.groups?.includes('Ops');

  // Desktop: collapsed unless pinned or hovered. Mobile drawer always expanded.
  const collapsed = !mobileOpen && !isPinned && !isHovered;

  useEffect(() => {
    let cancelled = false;
    if (!user?.idToken) {
      setPendingPayLatersCount(0);
      return undefined;
    }
    countPendingPayLaters(user.idToken)
      .then((n) => { if (!cancelled) setPendingPayLatersCount(n); })
      .catch(() => { if (!cancelled) setPendingPayLatersCount(0); });
    return () => { cancelled = true; };
  }, [location.pathname, user?.idToken]);

  return (
    <aside
      className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''} ${mobileOpen ? styles.mobileOpen : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.brand}>
        {collapsed ? (
          <img src={bhimaLogo} alt="Bhima Store" className={styles.brandLogoCollapsed} />
        ) : (
          <div className={styles.brandText}>
            <img src={bhimaLogo} alt="Bhima Store" className={styles.brandLogo} />
            <span className={styles.brandSub}>Ops Console</span>
          </div>
        )}
        <button
          type="button"
          className={styles.mobileCloseBtn}
          onClick={onMobileClose}
          aria-label="Close navigation"
        >
          <X size={18} />
        </button>
        <button
          className={`${styles.collapseBtn} ${isPinned ? styles.pinnedBtn : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setIsPinned((prev) => !prev);
          }}
          aria-label={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
          title={isPinned ? 'Unpin sidebar (auto-collapse on leave)' : 'Pin sidebar expanded'}
        >
          <Pin size={15} />
        </button>
      </div>

      <nav className={styles.nav}>
        <NavLink
          to="/"
          end
          className={({ isActive }) => `${styles.primaryLink} ${isActive ? styles.active : ''}`}
        >
          <LayoutGrid size={18} strokeWidth={2.1} />
          {!collapsed && <span>Order Queue</span>}
          {queueBadge > 0 && <span className={styles.liveBadge}>{queueBadge}</span>}
        </NavLink>

        <NavLink
          to="/pos"
          className={({ isActive }) => `${styles.primaryLink} ${isActive ? styles.active : ''}`}
        >
          <ShoppingCart size={18} strokeWidth={2.1} />
          {!collapsed && <span>Point of Sale</span>}
        </NavLink>

        <p className={styles.sectionLabel}>{!collapsed ? 'Operations' : '—'}</p>
        {opsLinks.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
          >
            <Icon size={17} strokeWidth={2} />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}

        <NavLink
          to="/returns"
          className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
        >
          <RotateCcw size={17} strokeWidth={2} />
          {!collapsed && <span>Return & Exchange</span>}
          {pendingReturnsCount > 0 && (
            <span className={styles.liveBadge} style={{ backgroundColor: '#e11d48' }}>
              {pendingReturnsCount}
            </span>
          )}
        </NavLink>

        <NavLink
          to="/pay-laters"
          className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
        >
          <Clock size={17} strokeWidth={2} />
          {!collapsed && <span>Pay Laters</span>}
          {pendingPayLatersCount > 0 && (
            <span className={styles.liveBadge} style={{ backgroundColor: '#e11d48' }}>
              {pendingPayLatersCount}
            </span>
          )}
        </NavLink>

        <NavLink
          to="/support"
          className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
        >
          <LifeBuoy size={17} strokeWidth={2} />
          {!collapsed && <span>Support Requests</span>}
          {(openTicketsCount > 0 || inProgressTicketsCount > 0) && (
            <span className={styles.ticketBadges}>
              {openTicketsCount > 0 && (
                <span
                  className={`${styles.countBadge} ${styles.countBadgeOpen}`}
                  title={`${openTicketsCount} open ticket${openTicketsCount === 1 ? '' : 's'}`}
                >
                  {openTicketsCount}
                </span>
              )}
              {inProgressTicketsCount > 0 && (
                <span
                  className={`${styles.countBadge} ${styles.countBadgeInProgress}`}
                  title={`${inProgressTicketsCount} in-progress ticket${inProgressTicketsCount === 1 ? '' : 's'}`}
                >
                  {inProgressTicketsCount}
                </span>
              )}
            </span>
          )}
        </NavLink>

        <p className={styles.sectionLabel}>{!collapsed ? 'Insights' : '—'}</p>
        {canSeeProductInsight && (
          <NavLink
            to="/product-insight"
            className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
          >
            <PackageSearch size={17} strokeWidth={2} />
            {!collapsed && <span>Product Insight</span>}
          </NavLink>
        )}
        <NavLink
          to="/analytics"
          className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
        >
          <BarChart3 size={17} strokeWidth={2} />
          {!collapsed && <span>Analytics</span>}
        </NavLink>

        <p className={styles.sectionLabel}>{!collapsed ? 'Growth' : '—'}</p>
        {growthLinks.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
          >
            <Icon size={17} strokeWidth={2} />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}

        <p className={styles.sectionLabel}>{!collapsed ? 'Store Settings' : '—'}</p>
        <NavLink
          to="/store-settings"
          className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
        >
          <Store size={17} strokeWidth={2} />
          {!collapsed && <span>Store Hours</span>}
        </NavLink>

        {isAdmin && (
          <>
            <p className={styles.sectionLabel}>{!collapsed ? 'Admin' : '—'}</p>
            <NavLink
              to="/admin"
              className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
            >
              <ShieldCheck size={17} strokeWidth={2} />
              {!collapsed && <span>Staff & Roles</span>}
            </NavLink>
            <NavLink
              to="/admin/deletion-requests"
              className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
            >
              <UserX size={17} strokeWidth={2} />
              {!collapsed && <span>Deletion Requests</span>}
            </NavLink>
          </>
        )}
      </nav>
    </aside>
  );
}

